import { useState, useMemo, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import SASidebar from '@/pages/sa/dashboard/components/SASidebar';
import {
  moderationItems,
  moderationMetrics,
  type ModerationItem,
  type ModerationCategory,
  type ModerationStatus,
} from '@/mocks/globalModeration';

type LoadingState = 'loading' | 'ready' | 'error';

function simulateLoad<T>(data: T): Promise<T> {
  return new Promise((resolve, reject) => {
    const d = 600 + Math.random() * 400;
    setTimeout(() => {
      if (Math.random() > 0.96) {
        reject(new Error('Timeout al conectar con el servidor.'));
      } else {
        resolve(data);
      }
    }, d);
  });
}

/* ── Helpers ── */
const categoryLabel: Record<ModerationCategory | 'todos', string> = {
  todos: 'Todos',
  chat: 'Chats',
  saludo: 'Saludos',
  comentario: 'Comentarios',
  concurso: 'Concursos',
  evento: 'Eventos',
};

const categoryIcon: Record<ModerationCategory, string> = {
  chat: 'ri-chat-3-line',
  saludo: 'ri-chat-smile-2-line',
  comentario: 'ri-message-2-line',
  concurso: 'ri-trophy-line',
  evento: 'ri-calendar-event-line',
};

const categoryColor: Record<ModerationCategory, { bg: string; text: string; iconBg: string }> = {
  chat: { bg: 'bg-sky-50', text: 'text-sky-600', iconBg: 'bg-sky-100' },
  saludo: { bg: 'bg-emerald-50', text: 'text-emerald-600', iconBg: 'bg-emerald-100' },
  comentario: { bg: 'bg-violet-50', text: 'text-violet-600', iconBg: 'bg-violet-100' },
  concurso: { bg: 'bg-amber-50', text: 'text-amber-600', iconBg: 'bg-amber-100' },
  evento: { bg: 'bg-rose-50', text: 'text-rose-600', iconBg: 'bg-rose-100' },
};

const statusLabel: Record<ModerationStatus, string> = {
  aprobado: 'Aprobado',
  pendiente: 'Pendiente',
  oculto: 'Oculto',
  bloqueado: 'Bloqueado',
};

const statusBadgeStyle: Record<ModerationStatus, { bg: string; text: string; dot: string }> = {
  aprobado: { bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500' },
  pendiente: { bg: 'bg-amber-50', text: 'text-amber-700', dot: 'bg-amber-500' },
  oculto: { bg: 'bg-foreground-100', text: 'text-foreground-500', dot: 'bg-foreground-400' },
  bloqueado: { bg: 'bg-red-50', text: 'text-red-600', dot: 'bg-red-500' },
};

/* ── Status Badge ── */
function StatusBadge({ status }: { status: ModerationStatus }) {
  const st = statusBadgeStyle[status];
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full ${st.bg} ${st.text} text-[10px] font-medium whitespace-nowrap`}>
      <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`}></span>
      {statusLabel[status]}
    </span>
  );
}

/* ── Category Badge ── */
function CategoryBadge({ category }: { category: ModerationCategory }) {
  const c = categoryColor[category];
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full ${c.bg} ${c.text} text-[10px] font-medium whitespace-nowrap`}>
      <i className={`${categoryIcon[category]} text-[9px]`}></i>
      {categoryLabel[category]}
    </span>
  );
}

/* ── KPI Card ── */
function KpiCard({ label, value, icon, bg, color }: { label: string; value: string; icon: string; bg: string; color: string }) {
  return (
    <div className="bg-background-50 border border-background-200 rounded-2xl p-4 flex items-center gap-3 hover:border-background-300 transition-colors">
      <div className={`w-10 h-10 rounded-xl ${bg} flex items-center justify-center shrink-0`}>
        <i className={`${icon} ${color} text-lg`}></i>
      </div>
      <div className="min-w-0">
        <p className="text-foreground-400 text-[10px] font-medium truncate">{label}</p>
        <p className="text-foreground-800 text-lg font-bold leading-tight">{value}</p>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════
   CATEGORY TAB
   ═══════════════════════════════════════════════ */

function CategoryTab({ cat, active, onClick, count, pendingCount }: {
  cat: ModerationCategory | 'todos';
  active: ModerationCategory | 'todos';
  onClick: (c: ModerationCategory | 'todos') => void;
  count: number;
  pendingCount: number;
}) {
  const isActive = active === cat;
  const isAll = cat === 'todos';
  const cKey = isAll ? null : cat;
  const c = cKey ? categoryColor[cKey] : null;

  return (
    <button
      onClick={() => onClick(cat)}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
        isActive
          ? 'bg-foreground-950 text-background-50'
          : 'bg-background-50 text-foreground-500 border border-background-200 hover:border-foreground-300 hover:text-foreground-700'
      }`}
    >
      {!isAll && cKey && <i className={`${categoryIcon[cKey]} text-[9px]`}></i>}
      {categoryLabel[cat]}
      <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-bold ${
        isActive ? 'bg-background-50/20 text-background-50' : 'bg-background-100 text-foreground-400'
      }`}>{count}</span>
      {pendingCount > 0 && (
        <span className="w-1.5 h-1.5 rounded-full bg-amber-500"></span>
      )}
    </button>
  );
}

/* ═══════════════════════════════════════════════
   CONFIRM DIALOG
   ═══════════════════════════════════════════════ */

function ConfirmDialog({ title, message, icon, iconBg, iconColor, confirmLabel, confirmClass, onConfirm, onCancel }: {
  title: string;
  message: string;
  icon: string;
  iconBg: string;
  iconColor: string;
  confirmLabel: string;
  confirmClass: string;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onCancel}></div>
      <div className="relative bg-background-50 rounded-2xl max-w-sm w-full shadow-lg p-6 text-center">
        <div className={`w-14 h-14 mx-auto mb-4 rounded-2xl ${iconBg} flex items-center justify-center`}>
          <i className={`${icon} ${iconColor} text-xl`}></i>
        </div>
        <h3 className="text-foreground-950 font-bold text-sm mb-1">{title}</h3>
        <p className="text-foreground-400 text-xs mb-5">{message}</p>
        <div className="flex items-center gap-2">
          <button
            onClick={onCancel}
            className="flex-1 px-4 py-2 rounded-xl bg-background-100 text-foreground-600 text-xs font-medium cursor-pointer hover:bg-background-200 transition-colors whitespace-nowrap"
          >
            Cancelar
          </button>
          <button
            onClick={onConfirm}
            className={`flex-1 px-4 py-2 rounded-xl text-background-50 text-xs font-bold cursor-pointer transition-colors whitespace-nowrap ${confirmClass}`}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════
   DETAIL MODAL
   ═══════════════════════════════════════════════ */

function DetailModal({ item, onClose, onAction }: {
  item: ModerationItem;
  onClose: () => void;
  onAction: (item: ModerationItem, action: 'aprobar' | 'ocultar' | 'eliminar' | 'banear') => void;
}) {
  const c = categoryColor[item.category];
  const st = statusBadgeStyle[item.status];

  return (
    <div className="fixed inset-0 z-[85] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose}></div>
      <div className="relative bg-background-50 rounded-2xl max-w-lg w-full max-h-[85vh] overflow-y-auto shadow-lg">
        {/* Header */}
        <div className="sticky top-0 bg-background-50 border-b border-background-200 p-5 flex items-start justify-between gap-4 z-10">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <CategoryBadge category={item.category} />
              <StatusBadge status={item.status} />
            </div>
            <p className="text-foreground-500 text-[10px]">
              ID #{item.id} · {item.date}
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center shrink-0 cursor-pointer hover:bg-background-200 transition-colors"
          >
            <i className="ri-close-line text-foreground-500 text-sm"></i>
          </button>
        </div>

        {/* Content */}
        <div className="p-5 space-y-4">
          {/* Content */}
          <div className="bg-background-100 rounded-xl p-4">
            <p className="text-foreground-800 text-sm leading-relaxed">{item.content}</p>
          </div>

          {/* Info grid */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-background-100 rounded-xl p-3">
              <p className="text-foreground-400 text-[9px] mb-0.5">Autor</p>
              <p className="text-foreground-800 text-xs font-semibold">{item.authorName}</p>
            </div>
            <div className="bg-background-100 rounded-xl p-3">
              <p className="text-foreground-400 text-[9px] mb-0.5">Radio</p>
              <p className="text-foreground-800 text-xs font-semibold">{item.radioName}</p>
              <p className="text-foreground-400 text-[9px]">{item.radioFrequency}</p>
            </div>
          </div>

          {/* Report info */}
          {item.reportCount > 0 && (
            <div className="bg-red-50 border border-red-100 rounded-xl p-3 space-y-2">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg bg-red-100 flex items-center justify-center">
                  <i className="ri-alert-line text-red-500 text-sm"></i>
                </div>
                <div>
                  <p className="text-red-700 text-xs font-semibold">{item.reportCount} reporte{item.reportCount > 1 ? 's' : ''}</p>
                  {item.reportedBy && <p className="text-red-500 text-[10px]">Reportado por: {item.reportedBy}</p>}
                </div>
              </div>
              {item.reportReason && (
                <p className="text-red-600 text-[10px] leading-relaxed">Motivo: {item.reportReason}</p>
              )}
            </div>
          )}
        </div>

        {/* Actions footer */}
        <div className="sticky bottom-0 bg-background-50 border-t border-background-200 p-4 flex items-center justify-between gap-2 flex-wrap">
          <button
            onClick={onClose}
            className="px-3 py-2 rounded-xl bg-background-100 text-foreground-600 text-xs font-medium cursor-pointer hover:bg-background-200 transition-colors whitespace-nowrap"
          >
            Cerrar
          </button>
          <div className="flex items-center gap-1.5 flex-wrap">
            {item.status !== 'aprobado' && (
              <button
                onClick={() => onAction(item, 'aprobar')}
                className="flex items-center gap-1 px-3 py-2 rounded-xl bg-emerald-50 text-emerald-700 text-xs font-medium cursor-pointer hover:bg-emerald-100 transition-colors whitespace-nowrap"
              >
                <i className="ri-check-line text-sm"></i>
                Aprobar
              </button>
            )}
            {item.status !== 'oculto' && item.status !== 'bloqueado' && (
              <button
                onClick={() => onAction(item, 'ocultar')}
                className="flex items-center gap-1 px-3 py-2 rounded-xl bg-foreground-100 text-foreground-600 text-xs font-medium cursor-pointer hover:bg-foreground-200 transition-colors whitespace-nowrap"
              >
                <i className="ri-eye-off-line text-sm"></i>
                Ocultar
              </button>
            )}
            {item.status !== 'bloqueado' && (
              <button
                onClick={() => onAction(item, 'banear')}
                className="flex items-center gap-1 px-3 py-2 rounded-xl bg-red-50 text-red-600 text-xs font-medium cursor-pointer hover:bg-red-100 transition-colors whitespace-nowrap"
              >
                <i className="ri-forbid-line text-sm"></i>
                Banear
              </button>
            )}
            <button
              onClick={() => onAction(item, 'eliminar')}
              className="flex items-center gap-1 px-3 py-2 rounded-xl bg-red-100 text-red-700 text-xs font-medium cursor-pointer hover:bg-red-200 transition-colors whitespace-nowrap"
            >
              <i className="ri-delete-bin-line text-sm"></i>
              Eliminar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════
   MODERATION ITEM CARD
   ═══════════════════════════════════════════════ */

function ModerationCard({ item, onView, onAction }: {
  item: ModerationItem;
  onView: (item: ModerationItem) => void;
  onAction: (item: ModerationItem, action: 'aprobar' | 'ocultar' | 'eliminar' | 'banear') => void;
}) {
  const [showActions, setShowActions] = useState(false);
  const c = categoryColor[item.category];

  return (
    <div className="bg-background-50 border border-background-200 rounded-2xl p-4 hover:border-background-300 transition-all group space-y-3">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className={`w-10 h-10 rounded-xl ${c.iconBg} flex items-center justify-center shrink-0`}>
            <i className={`${categoryIcon[item.category]} ${c.text} text-sm`}></i>
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <p className="text-foreground-800 text-xs font-semibold line-clamp-1">{item.authorName}</p>
              <CategoryBadge category={item.category} />
            </div>
            <p className="text-foreground-400 text-[9px] mt-0.5">{item.radioName} · {item.radioFrequency}</p>
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <StatusBadge status={item.status} />
        </div>
      </div>

      {/* Content preview */}
      <div className="bg-background-100 rounded-xl p-3">
        <p className="text-foreground-700 text-xs leading-relaxed line-clamp-3">{item.content}</p>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between pt-1 border-t border-background-100">
        <div className="flex items-center gap-3">
          <span className="text-foreground-400 text-[9px] flex items-center gap-1">
            <i className="ri-calendar-line text-[9px]"></i>
            {item.date}
          </span>
          {item.reportCount > 0 && (
            <span className="text-red-500 text-[9px] flex items-center gap-1 font-medium">
              <i className="ri-alert-line text-[9px]"></i>
              {item.reportCount}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          {/* Quick actions */}
          {item.status !== 'aprobado' && (
            <button
              onClick={() => onAction(item, 'aprobar')}
              className="w-7 h-7 rounded-lg bg-emerald-50 flex items-center justify-center cursor-pointer hover:bg-emerald-100 transition-colors"
              title="Aprobar"
            >
              <i className="ri-check-line text-emerald-600 text-xs"></i>
            </button>
          )}
          {item.status !== 'oculto' && item.status !== 'bloqueado' && (
            <button
              onClick={() => onAction(item, 'ocultar')}
              className="w-7 h-7 rounded-lg bg-foreground-100 flex items-center justify-center cursor-pointer hover:bg-foreground-200 transition-colors"
              title="Ocultar"
            >
              <i className="ri-eye-off-line text-foreground-500 text-xs"></i>
            </button>
          )}
          {item.status !== 'bloqueado' && (
            <button
              onClick={() => onAction(item, 'banear')}
              className="w-7 h-7 rounded-lg bg-red-50 flex items-center justify-center cursor-pointer hover:bg-red-100 transition-colors"
              title="Banear"
            >
              <i className="ri-forbid-line text-red-500 text-xs"></i>
            </button>
          )}
          <button
            onClick={() => onAction(item, 'eliminar')}
            className="w-7 h-7 rounded-lg bg-red-50 flex items-center justify-center cursor-pointer hover:bg-red-100 transition-colors"
            title="Eliminar"
          >
            <i className="ri-delete-bin-line text-red-500 text-xs"></i>
          </button>
          <div className="w-px h-5 bg-background-200 mx-0.5"></div>
          <div className="relative">
            <button
              onClick={() => setShowActions(!showActions)}
              className="w-7 h-7 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors"
            >
              <i className={`${showActions ? 'ri-close-line' : 'ri-more-2-line'} text-foreground-500 text-xs`}></i>
            </button>
            {showActions && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setShowActions(false)}></div>
                <div className="absolute right-0 bottom-full mb-1 z-20 bg-background-50 border border-background-200 rounded-xl shadow-lg py-1 min-w-[120px]">
                  <button onClick={() => { setShowActions(false); onView(item); }} className="w-full flex items-center gap-2 px-3 py-2 text-xs text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap">
                    <i className="ri-eye-line text-sm"></i>Ver detalle
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Loading Skeleton ── */
function LoadingSkeleton() {
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="bg-background-50 border border-background-200 rounded-2xl p-4 flex items-center gap-3 animate-pulse">
            <div className="w-10 h-10 rounded-xl bg-background-200"></div>
            <div className="space-y-1">
              <div className="w-16 h-2.5 bg-background-200 rounded"></div>
              <div className="w-10 h-4 bg-background-200 rounded"></div>
            </div>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {Array.from({ length: 9 }).map((_, i) => (
          <div key={i} className="bg-background-50 border border-background-200 rounded-2xl p-4 space-y-3 animate-pulse">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-background-200"></div>
              <div className="space-y-1 flex-1">
                <div className="w-1/2 h-3 bg-background-200 rounded"></div>
                <div className="w-1/3 h-2 bg-background-200 rounded"></div>
              </div>
            </div>
            <div className="h-16 bg-background-200 rounded-xl"></div>
            <div className="flex justify-between">
              <div className="w-20 h-2.5 bg-background-200 rounded"></div>
              <div className="flex gap-1">
                {Array.from({ length: 5 }).map((_, j) => (
                  <div key={j} className="w-7 h-7 bg-background-200 rounded-lg"></div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── Error State ── */
function ErrorState({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="bg-background-50 border border-background-200 rounded-2xl p-12 text-center">
      <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-red-50 flex items-center justify-center">
        <i className="ri-error-warning-line text-red-400 text-2xl"></i>
      </div>
      <p className="text-foreground-800 text-sm font-semibold mb-1">Error al cargar</p>
      <p className="text-foreground-400 text-xs mb-4">{message}</p>
      <button
        onClick={onRetry}
        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-foreground-950 text-background-50 text-xs font-bold hover:bg-foreground-800 transition-colors cursor-pointer"
      >
        <i className="ri-refresh-line text-sm"></i>
        Reintentar
      </button>
    </div>
  );
}

/* ── Empty State ── */
function EmptyState({ onClear }: { onClear: () => void }) {
  return (
    <div className="bg-background-50 border border-background-200 rounded-2xl p-12 text-center">
      <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-background-100 flex items-center justify-center">
        <i className="ri-shield-check-line text-foreground-300 text-xl"></i>
      </div>
      <p className="text-foreground-500 text-sm font-medium">Sin resultados</p>
      <p className="text-foreground-300 text-xs mt-1">No hay contenido que coincida con los filtros actuales</p>
      <button onClick={onClear} className="mt-4 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-background-100 text-foreground-500 text-[10px] font-medium hover:bg-background-200 transition-colors cursor-pointer">
        <i className="ri-filter-off-line text-xs"></i>
        Limpiar filtros
      </button>
    </div>
  );
}

/* ── Summary Bar ── */
function SummaryBar({ total, pending, reported }: { total: number; pending: number; reported: number }) {
  return (
    <div className="bg-background-50 border border-background-200 rounded-2xl p-3.5 flex flex-wrap items-center gap-4">
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 rounded-lg bg-primary-50 flex items-center justify-center">
          <i className="ri-shield-line text-primary-500 text-sm"></i>
        </div>
        <div>
          <p className="text-foreground-400 text-[9px]">Total Contenido</p>
          <p className="text-foreground-800 text-sm font-bold">{total}</p>
        </div>
      </div>
      <div className="w-px h-8 bg-background-200 hidden sm:block"></div>
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center">
          <i className="ri-time-line text-amber-500 text-sm"></i>
        </div>
        <div>
          <p className="text-foreground-400 text-[9px]">Pendientes</p>
          <p className="text-foreground-800 text-sm font-bold">{pending}</p>
        </div>
      </div>
      <div className="w-px h-8 bg-background-200 hidden sm:block"></div>
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 rounded-lg bg-red-50 flex items-center justify-center">
          <i className="ri-alert-line text-red-500 text-sm"></i>
        </div>
        <div>
          <p className="text-foreground-400 text-[9px]">Reportados</p>
          <p className="text-foreground-800 text-sm font-bold">{reported}</p>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════
   MAIN PAGE
   ═══════════════════════════════════════════════ */

export default function SAGlobalModeration() {
  const [items, setItems] = useState<ModerationItem[]>([]);
  const [loadingState, setLoadingState] = useState<LoadingState>('loading');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<ModerationCategory | 'todos'>('todos');
  const [statusFilter, setStatusFilter] = useState<ModerationStatus | 'todos'>('todos');
  const [searchTerm, setSearchTerm] = useState('');

  /* Modal states */
  const [detailItem, setDetailItem] = useState<ModerationItem | null>(null);
  const [confirmAction, setConfirmAction] = useState<{ item: ModerationItem; action: 'aprobar' | 'ocultar' | 'eliminar' | 'banear' } | null>(null);

  const loadData = useCallback(() => {
    setLoadingState('loading');
    setErrorMsg(null);
    simulateLoad(moderationItems)
      .then((data) => {
        setItems(data);
        setLoadingState('ready');
      })
      .catch((err) => {
        setErrorMsg(err.message || 'Error desconocido');
        setLoadingState('error');
      });
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  /* Filtered items */
  const filteredItems = useMemo(() => {
    let result = items;
    if (categoryFilter !== 'todos') {
      result = result.filter((i) => i.category === categoryFilter);
    }
    if (statusFilter !== 'todos') {
      result = result.filter((i) => i.status === statusFilter);
    }
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      result = result.filter(
        (i) =>
          i.content.toLowerCase().includes(q) ||
          i.authorName.toLowerCase().includes(q) ||
          i.radioName.toLowerCase().includes(q) ||
          (i.reportReason && i.reportReason.toLowerCase().includes(q)),
      );
    }
    return result;
  }, [items, categoryFilter, statusFilter, searchTerm]);

  /* Computed metrics */
  const computedMetrics = useMemo(() => ({
    total: filteredItems.length,
    pending: filteredItems.filter((i) => i.status === 'pendiente').length,
    reported: filteredItems.filter((i) => i.reportCount > 0).length,
    approved: filteredItems.filter((i) => i.status === 'aprobado').length,
    hidden: filteredItems.filter((i) => i.status === 'oculto').length,
    blocked: filteredItems.filter((i) => i.status === 'bloqueado').length,
  }), [filteredItems]);

  /* Category counts */
  const categoryCounts = useMemo(() => ({
    todos: items.length,
    chat: items.filter((i) => i.category === 'chat').length,
    saludo: items.filter((i) => i.category === 'saludo').length,
    comentario: items.filter((i) => i.category === 'comentario').length,
    concurso: items.filter((i) => i.category === 'concurso').length,
    evento: items.filter((i) => i.category === 'evento').length,
  }), [items]);

  const pendingByCategory = useMemo(() => ({
    todos: items.filter((i) => i.status === 'pendiente').length,
    chat: items.filter((i) => i.category === 'chat' && i.status === 'pendiente').length,
    saludo: items.filter((i) => i.category === 'saludo' && i.status === 'pendiente').length,
    comentario: items.filter((i) => i.category === 'comentario' && i.status === 'pendiente').length,
    concurso: items.filter((i) => i.category === 'concurso' && i.status === 'pendiente').length,
    evento: items.filter((i) => i.category === 'evento' && i.status === 'pendiente').length,
  }), [items]);

  /* Status counts for filter chips */
  const statusCounts = useMemo(() => ({
    todos: items.filter((i) => categoryFilter === 'todos' ? true : i.category === categoryFilter).length,
    pendiente: items.filter((i) => (categoryFilter === 'todos' ? true : i.category === categoryFilter) && i.status === 'pendiente').length,
    aprobado: items.filter((i) => (categoryFilter === 'todos' ? true : i.category === categoryFilter) && i.status === 'aprobado').length,
    oculto: items.filter((i) => (categoryFilter === 'todos' ? true : i.category === categoryFilter) && i.status === 'oculto').length,
    bloqueado: items.filter((i) => (categoryFilter === 'todos' ? true : i.category === categoryFilter) && i.status === 'bloqueado').length,
  }), [items, categoryFilter]);

  /* Handle action */
  const handleAction = useCallback((item: ModerationItem, action: 'aprobar' | 'ocultar' | 'eliminar' | 'banear') => {
    if (action === 'eliminar') {
      setConfirmAction({ item, action });
    } else {
      setConfirmAction({ item, action });
    }
  }, []);

  const executeAction = useCallback(() => {
    if (!confirmAction) return;
    const { item, action } = confirmAction;

    if (action === 'eliminar') {
      setItems((prev) => prev.filter((i) => i.id !== item.id));
    } else {
      const newStatus: ModerationStatus = action === 'aprobar' ? 'aprobado' : action === 'ocultar' ? 'oculto' : 'bloqueado';
      setItems((prev) =>
        prev.map((i) => (i.id === item.id ? { ...i, status: newStatus } : i))
      );
    }
    setConfirmAction(null);
  }, [confirmAction]);

  const clearAllFilters = () => {
    setCategoryFilter('todos');
    setStatusFilter('todos');
    setSearchTerm('');
  };

  /* Confirm dialog config */
  const confirmConfig = useMemo(() => {
    if (!confirmAction) return null;
    const { action } = confirmAction;
    switch (action) {
      case 'aprobar':
        return {
          title: '¿Aprobar contenido?',
          message: 'El contenido será marcado como aprobado y será visible para todos.',
          icon: 'ri-check-line',
          iconBg: 'bg-emerald-50',
          iconColor: 'text-emerald-500',
          confirmLabel: 'Sí, Aprobar',
          confirmClass: 'bg-emerald-600 hover:bg-emerald-700',
        };
      case 'ocultar':
        return {
          title: '¿Ocultar contenido?',
          message: 'El contenido será ocultado. Solo los moderadores podrán verlo.',
          icon: 'ri-eye-off-line',
          iconBg: 'bg-foreground-100',
          iconColor: 'text-foreground-500',
          confirmLabel: 'Sí, Ocultar',
          confirmClass: 'bg-foreground-800 hover:bg-foreground-950',
        };
      case 'eliminar':
        return {
          title: '¿Eliminar contenido?',
          message: 'Esta acción es permanente. El contenido se eliminará definitivamente.',
          icon: 'ri-delete-bin-line',
          iconBg: 'bg-red-50',
          iconColor: 'text-red-500',
          confirmLabel: 'Sí, Eliminar',
          confirmClass: 'bg-red-600 hover:bg-red-700',
        };
      case 'banear':
        return {
          title: '¿Banear contenido y autor?',
          message: 'El contenido será bloqueado y el autor será baneado. No podrá volver a interactuar.',
          icon: 'ri-forbid-line',
          iconBg: 'bg-red-50',
          iconColor: 'text-red-500',
          confirmLabel: 'Sí, Banear',
          confirmClass: 'bg-red-600 hover:bg-red-700',
        };
    }
  }, [confirmAction]);

  return (
    <div className="min-h-screen bg-background-100">
      <div className="flex">
        <SASidebar />
        <main className="flex-1 min-w-0">
          <div className="p-4 md:p-6 lg:p-8 pb-8 space-y-5 max-w-[1600px] mx-auto">
            {/* Breadcrumbs */}
            <div className="flex items-center gap-1.5 text-[10px] text-foreground-400">
              <Link to="/sa/dashboard" className="hover:text-foreground-600 transition-colors cursor-pointer">Dashboard</Link>
              <i className="ri-arrow-right-s-line text-[9px]"></i>
              <span className="text-foreground-500 font-medium">Moderación Global</span>
            </div>

            {/* Page header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-[10px] font-semibold mb-2">
                  <i className="ri-shield-check-line text-xs"></i>
                  Bloque SA-17
                </div>
                <h2 className="text-foreground-950 font-bold text-lg">Moderación Global</h2>
                <p className="text-foreground-400 text-xs mt-0.5">
                  Centro único de moderación · Chats, Saludos, Comentarios, Concursos y Eventos ·{' '}
                  {loadingState === 'ready' ? `${items.length} elementos` : 'Cargando...'}
                </p>
              </div>
              <div className="relative w-full sm:w-64">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-xs"></i>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Buscar contenido, autor, radio..."
                  disabled={loadingState !== 'ready'}
                  className="w-full pl-8 pr-8 py-2 bg-background-50 border border-background-200 rounded-xl text-xs text-foreground-800 placeholder:text-foreground-300 outline-none focus:border-foreground-300 transition-colors disabled:opacity-50"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 w-5 h-5 rounded-md bg-background-200 flex items-center justify-center cursor-pointer hover:bg-background-300 transition-colors"
                  >
                    <i className="ri-close-line text-foreground-500 text-[10px]"></i>
                  </button>
                )}
              </div>
            </div>

            {/* Summary Bar */}
            {loadingState === 'ready' && (
              <SummaryBar total={computedMetrics.total} pending={computedMetrics.pending} reported={computedMetrics.reported} />
            )}

            {/* KPIs */}
            {loadingState === 'ready' && (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
                <KpiCard label="Total" value={String(computedMetrics.total)} icon="ri-shield-line" bg="bg-primary-50" color="text-primary-500" />
                <KpiCard label="Pendientes" value={String(computedMetrics.pending)} icon="ri-time-line" bg="bg-amber-50" color="text-amber-500" />
                <KpiCard label="Aprobados" value={String(computedMetrics.approved)} icon="ri-check-line" bg="bg-emerald-50" color="text-emerald-500" />
                <KpiCard label="Ocultos" value={String(computedMetrics.hidden)} icon="ri-eye-off-line" bg="bg-foreground-100" color="text-foreground-500" />
                <KpiCard label="Bloqueados" value={String(computedMetrics.blocked)} icon="ri-forbid-line" bg="bg-red-50" color="text-red-500" />
                <KpiCard label="Reportados" value={String(computedMetrics.reported)} icon="ri-alert-line" bg="bg-rose-50" color="text-rose-500" />
              </div>
            )}

            {/* Loading KPIs */}
            {loadingState === 'loading' && (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="bg-background-50 border border-background-200 rounded-2xl p-4 flex items-center gap-3 animate-pulse">
                    <div className="w-10 h-10 rounded-xl bg-background-200"></div>
                    <div className="space-y-1">
                      <div className="w-16 h-2.5 bg-background-200 rounded"></div>
                      <div className="w-10 h-4 bg-background-200 rounded"></div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Filters */}
            {loadingState === 'ready' && (
              <div className="space-y-3">
                {/* Category tabs */}
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="text-foreground-400 text-[9px] font-medium mr-1">Categoría:</span>
                  <CategoryTab cat="todos" active={categoryFilter} onClick={setCategoryFilter} count={categoryCounts.todos} pendingCount={pendingByCategory.todos} />
                  <CategoryTab cat="chat" active={categoryFilter} onClick={setCategoryFilter} count={categoryCounts.chat} pendingCount={pendingByCategory.chat} />
                  <CategoryTab cat="saludo" active={categoryFilter} onClick={setCategoryFilter} count={categoryCounts.saludo} pendingCount={pendingByCategory.saludo} />
                  <CategoryTab cat="comentario" active={categoryFilter} onClick={setCategoryFilter} count={categoryCounts.comentario} pendingCount={pendingByCategory.comentario} />
                  <CategoryTab cat="concurso" active={categoryFilter} onClick={setCategoryFilter} count={categoryCounts.concurso} pendingCount={pendingByCategory.concurso} />
                  <CategoryTab cat="evento" active={categoryFilter} onClick={setCategoryFilter} count={categoryCounts.evento} pendingCount={pendingByCategory.evento} />
                </div>

                {/* Status filter */}
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="text-foreground-400 text-[9px] font-medium mr-1">Estado:</span>
                  <button
                    onClick={() => setStatusFilter('todos')}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                      statusFilter === 'todos' ? 'bg-foreground-950 text-background-50' : 'bg-background-50 text-foreground-500 border border-background-200 hover:border-foreground-300'
                    }`}
                  >
                    Todos
                    <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-bold ${statusFilter === 'todos' ? 'bg-background-50/20 text-background-50' : 'bg-background-100 text-foreground-400'}`}>{statusCounts.todos}</span>
                  </button>
                  <button
                    onClick={() => setStatusFilter('pendiente')}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                      statusFilter === 'pendiente' ? 'bg-amber-600 text-background-50' : 'bg-background-50 text-foreground-500 border border-background-200 hover:border-amber-300'
                    }`}
                  >
                    Pendientes
                    <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-bold ${statusFilter === 'pendiente' ? 'bg-background-50/20 text-background-50' : 'bg-background-100 text-foreground-400'}`}>{statusCounts.pendiente}</span>
                  </button>
                  <button
                    onClick={() => setStatusFilter('aprobado')}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                      statusFilter === 'aprobado' ? 'bg-emerald-600 text-background-50' : 'bg-background-50 text-foreground-500 border border-background-200 hover:border-emerald-300'
                    }`}
                  >
                    Aprobados
                    <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-bold ${statusFilter === 'aprobado' ? 'bg-background-50/20 text-background-50' : 'bg-background-100 text-foreground-400'}`}>{statusCounts.aprobado}</span>
                  </button>
                  <button
                    onClick={() => setStatusFilter('oculto')}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                      statusFilter === 'oculto' ? 'bg-foreground-600 text-background-50' : 'bg-background-50 text-foreground-500 border border-background-200 hover:border-foreground-300'
                    }`}
                  >
                    Ocultos
                    <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-bold ${statusFilter === 'oculto' ? 'bg-background-50/20 text-background-50' : 'bg-background-100 text-foreground-400'}`}>{statusCounts.oculto}</span>
                  </button>
                  <button
                    onClick={() => setStatusFilter('bloqueado')}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                      statusFilter === 'bloqueado' ? 'bg-red-600 text-background-50' : 'bg-background-50 text-foreground-500 border border-background-200 hover:border-red-300'
                    }`}
                  >
                    Bloqueados
                    <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-bold ${statusFilter === 'bloqueado' ? 'bg-background-50/20 text-background-50' : 'bg-background-100 text-foreground-400'}`}>{statusCounts.bloqueado}</span>
                  </button>
                </div>
              </div>
            )}

            {/* Content */}
            {loadingState === 'loading' && <LoadingSkeleton />}

            {loadingState === 'error' && <ErrorState message={errorMsg || 'Error'} onRetry={loadData} />}

            {loadingState === 'ready' && filteredItems.length === 0 && (
              <EmptyState onClear={clearAllFilters} />
            )}

            {loadingState === 'ready' && filteredItems.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {filteredItems.map((item) => (
                  <ModerationCard
                    key={item.id}
                    item={item}
                    onView={setDetailItem}
                    onAction={handleAction}
                  />
                ))}
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Mobile bottom hint */}
      <div className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-background-50/98 backdrop-blur-2xl border-t border-background-200 py-3 px-4 safe-bottom">
        <p className="text-center text-foreground-400 text-[10px] font-medium">Super Admin · Moderación Global</p>
      </div>

      {/* Detail Modal */}
      {detailItem && (
        <DetailModal
          item={detailItem}
          onClose={() => setDetailItem(null)}
          onAction={(item, action) => {
            setDetailItem(null);
            handleAction(item, action);
          }}
        />
      )}

      {/* Confirm Dialog */}
      {confirmAction && confirmConfig && (
        <ConfirmDialog
          title={confirmConfig.title}
          message={confirmConfig.message}
          icon={confirmConfig.icon}
          iconBg={confirmConfig.iconBg}
          iconColor={confirmConfig.iconColor}
          confirmLabel={confirmConfig.confirmLabel}
          confirmClass={confirmConfig.confirmClass}
          onConfirm={executeAction}
          onCancel={() => setConfirmAction(null)}
        />
      )}
    </div>
  );
}